import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Container, Snackbar, Typography } from '@mui/material';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import CallSplitOutlinedIcon from '@mui/icons-material/CallSplitOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import PhoneForwardedOutlinedIcon from '@mui/icons-material/PhoneForwardedOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import Page from 'src/components/Page';
import axios from 'src/utils/axios';

import DateToolbar from 'src/components/BpoMutabakat/DateToolbar';
import TabBar from 'src/components/BpoMutabakat/TabBar';
import KpiStrip from 'src/components/BpoMutabakat/KpiStrip';
import { getDefaultDate, toIsoDate } from 'src/components/BpoMutabakat/format';

import Overview from './tabs/Overview';
import QueueSkill from './tabs/QueueSkill';
import AgentPerformance from './tabs/AgentPerformance';
import Outbound from './tabs/Outbound';
import CallDetail from './tabs/CallDetail';
import QualityIpsos from './tabs/QualityIpsos';
import DataHealth from './tabs/DataHealth';

/** Başlığın altındaki ipucu şeridi doldururken sekmenin ne içerdiğini de söylüyor. */
const TABS = [
  { key: 'overview', label: 'Genel Bakış', hint: 'kuyruk, kanal, gün içi', icon: <DashboardOutlinedIcon /> },
  { key: 'skill', label: 'Kuyruk & Skill', hint: 'servis seviyesi', icon: <CallSplitOutlinedIcon /> },
  { key: 'agent', label: 'Agent Performans', hint: 'doluluk, kapasite', icon: <SupportAgentOutlinedIcon /> },
  { key: 'outbound', label: 'Outbound', hint: 'arama sonuçları', icon: <PhoneForwardedOutlinedIcon /> },
  { key: 'calls', label: 'Çağrı Detayı', hint: 'numara, kayıt', icon: <ListAltOutlinedIcon /> },
  { key: 'quality', label: 'Kalite & Ipsos', hint: 'puan, memnuniyet', icon: <VerifiedOutlinedIcon /> },
  { key: 'health', label: 'Veri Sağlığı', hint: 'yükleme durumu', icon: <FactCheckOutlinedIcon /> }
];

/**
 * Ekranın açılışta çektiği uçlar. Her biri
 * `Scripts/BpoMutabakat_SP.sql` içindeki bir stored procedure'e karşılık gelir.
 *
 * `empty`, uç hata verdiğinde bileşenlerin patlamaması için konan boş değer.
 */
const ENDPOINTS = [
  { key: 'summary', label: 'Özet', path: 'GetBpoMutabakatSummary', empty: null },
  { key: 'intervals', label: 'Gün içi dağılım', path: 'GetBpoMutabakatIntervals', empty: [] },
  { key: 'channels', label: 'Kanal', path: 'GetBpoMutabakatChannels', empty: [] },
  { key: 'queues', label: 'Kuyruk', path: 'GetBpoMutabakatQueues', empty: [] },
  { key: 'reports', label: 'Rapor durumları', path: 'GetBpoMutabakatReportStatuses', empty: [] },
  { key: 'lastSevenDays', label: 'Son 7 gün', path: 'GetBpoMutabakatLastSevenDays', empty: [] }
];

const BpoMutabakat = () => {
  const [date, setDate] = useState(getDefaultDate());
  const [loadedDate, setLoadedDate] = useState(toIsoDate(getDefaultDate()));
  const [tabIndex, setTabIndex] = useState(0);

  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);

  const load = useCallback((selected) => {
    const day = toIsoDate(selected);
    if (!day) {
      setAlert({ severity: 'warning', message: 'Geçerli bir tarih seçin.' });
      return;
    }

    setLoading(true);
    setAlert(null);

    // allSettled: uçlardan biri yayına alınmamışsa diğerlerinin verisi yine de
    // ekrana gelsin, sayfa tamamen boş kalmasın.
    Promise.allSettled(ENDPOINTS.map((e) => axios.get(`/${e.path}/${day}`)))
      .then((results) => {
        const next = {};
        const failed = [];

        results.forEach((result, i) => {
          const endpoint = ENDPOINTS[i];
          if (result.status === 'fulfilled') {
            const body = result.value?.data;
            next[endpoint.key] = body === undefined || body === null ? endpoint.empty : body;
          } else {
            next[endpoint.key] = endpoint.empty;
            failed.push({ endpoint, error: result.reason });
          }
        });

        setData(next);
        setLoadedDate(day);

        if (failed.length === ENDPOINTS.length) {
          const notFound = failed.every((f) => f.error?.response?.status === 404);
          setAlert({
            severity: 'error',
            message: notFound
              ? 'BPO Mutabakat servisleri henüz yayında değil.'
              : 'Veriler alınamadı. Lütfen tekrar deneyin.'
          });
        } else if (failed.length) {
          setAlert({
            severity: 'warning',
            message: `Bazı bloklar yüklenemedi: ${failed.map((f) => f.endpoint.label).join(', ')}.`
          });
        }
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load(getDefaultDate());
  }, [load]);

  const activeTab = TABS[tabIndex].key;

  return (
    <Page title="BPO Mutabakat">
      <Container maxWidth="xl" sx={{ py: 2 }}>
        <Typography variant="h3" sx={{ mb: 2 }}>
          BPO Mutabakat
        </Typography>

        <DateToolbar
          date={date}
          loadedDate={loadedDate}
          loading={loading}
          onChange={setDate}
          onSearch={load}
        />

        <KpiStrip summary={data.summary} loading={loading} />

        <TabBar tabs={TABS} value={tabIndex} onChange={setTabIndex} />

        {activeTab === 'overview' && (
          <Overview
            date={loadedDate}
            summary={data.summary}
            intervals={data.intervals}
            channels={data.channels}
            queues={data.queues}
            loading={loading}
          />
        )}
        {activeTab === 'skill' && <QueueSkill date={loadedDate} />}
        {activeTab === 'agent' && <AgentPerformance date={loadedDate} />}
        {activeTab === 'outbound' && <Outbound date={loadedDate} />}
        {activeTab === 'calls' && <CallDetail date={loadedDate} />}
        {activeTab === 'quality' && <QualityIpsos date={loadedDate} />}
        {activeTab === 'health' && (
          <DataHealth
            summary={data.summary}
            intervals={data.intervals}
            reports={data.reports}
            lastSevenDays={data.lastSevenDays}
            loading={loading}
          />
        )}

        <Snackbar
          open={!!alert}
          autoHideDuration={8000}
          onClose={() => setAlert(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert severity={alert?.severity || 'info'} onClose={() => setAlert(null)}>
            {alert?.message}
          </Alert>
        </Snackbar>
      </Container>
    </Page>
  );
};

export default BpoMutabakat;
